import {
  type InstrumentSource,
  resolveInstrumentBySymbol,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { isInverse, isPerpetual } from '@/lib/instrument-util'
import { queryClient } from '@/provider/query-provider'
import type { fetchInstrumentList } from '@/service/instrument.api'
import { instrumentKeys } from '@/service/instrument.hook'
import { Denomination } from '@/types/generated/common/trading'
import { type Exchange, type Instrument, InstrumentType } from '@/types/generated/common/types'

// Metadata first, like isInverseInstrument — but its symbol fallback calls every
// dash-or-plain symbol inverse (KRW-BTC, BTCUSDT), which would hide exactly the spot
// quote amounts this labels, so the fallback here only counts contract symbols.
function isInverseContract(symbol: string, instrument: Instrument | null): boolean {
  if (instrument?.type === InstrumentType.INSTRUMENT_TYPE_INVERSE) return true
  if (instrument?.type === InstrumentType.INSTRUMENT_TYPE_LINEAR) return false
  const isDatedFuture = /\d{1,2}[A-Z]{3}\d{2}/i.test(symbol)
  return (isPerpetual(symbol) || isDatedFuture) && isInverse(symbol)
}

/**
 * Unit shared by an order's `quantity` and `filledQuantity`. Null for UNSPECIFIED (rows
 * saved before the field existed), so callers keep their pre-existing behaviour.
 */
export function orderQuantitySide(denomination: Denomination | undefined): 'base' | 'quote' | null {
  if (denomination === Denomination.DENOMINATION_QUOTE) return 'quote'
  if (denomination === Denomination.DENOMINATION_BASE) return 'base'
  return null
}

function isQuoteDenominated(denomination: Denomination | undefined): boolean {
  return denomination === Denomination.DENOMINATION_QUOTE
}

/** Currency an order's quantity is in: quote for QUOTE orders, base otherwise. */
export function orderQuantityCurrency(
  denomination: Denomination | undefined,
  baseCurrency: string,
  quoteCurrency: string
): string {
  return isQuoteDenominated(denomination) ? quoteCurrency : baseCurrency
}

/**
 * Unit suffix for surfaces that print order sizes as bare numbers (tables, toasts).
 * Those numbers read as the instrument's conventional size unit — base for spot and
 * linear, USD for inverse contracts — so only a quote amount on a non-inverse
 * instrument (e.g. 10,000 KRW on an Upbit market buy) needs its currency spelled
 * out. Returns null when the bare number is already unambiguous.
 */
export function quoteQuantityUnit(
  denomination: Denomination | undefined,
  source: InstrumentSource,
  symbol: string
): string | null {
  if (!isQuoteDenominated(denomination)) return null
  const inst = resolveInstrumentBySymbol(source, symbol)
  if (isInverseContract(symbol, inst)) return null
  return resolveQuoteCurrency(source, symbol, inst)
}

/**
 * The instrument catalog already cached for `exchange`, for call sites outside React
 * (notification classification, toasts) that can't subscribe to it. Never fetches;
 * an empty list just means callers fall back to parsing the symbol.
 */
function cachedInstruments(exchange: Exchange): Instrument[] {
  return (
    queryClient.getQueryData<Awaited<ReturnType<typeof fetchInstrumentList>>>(
      instrumentKeys.list(exchange)
    )?.instruments ?? []
  )
}

/** A plain-text order quantity, with its quote currency appended when `quoteQuantityUnit` says so. */
export function formatOrderQuantity(
  order: { denomination?: Denomination; exchange: Exchange; symbol: string },
  quantity: string
): string {
  const unit = quoteQuantityUnit(
    order.denomination,
    cachedInstruments(order.exchange),
    order.symbol
  )
  return unit ? `${quantity} ${unit}` : quantity
}
